import React, { useCallback, useEffect, useRef, useState } from 'react'
import { sectionIds } from './sectionIds';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// import DarkMode from '../mode/DarkMode';
// import resume from "../../assets/Darshik'sCV.pdf"
import resume from "../../assets/Darshik_Rajpara.pdf"
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

const Header = ({ navbarOpen, setNavbarOpen }) => {
    // We'll use a new ref for the element we want to animate (the <nav> element)
    // const navAnimationRef = useRef(null);
    const navbarRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const [activeLink, setActiveLink] = useState(sectionIds[0] || ""); // default to first if available

    // ===============================
    // GSAP HEADER ANIMATION
    // ===============================
    useGSAP(() => {
        const tl = gsap.timeline({ defaults: { ease: "power2.out" }, delay: 1 });

        tl.from(navbarRef.current, {
            y: -50,
            opacity: 0,
            scale: 0.95,
            duration: 1,
            ease: "power3.out",
        }
        )
            .from(".header-logo-text", {
                opacity: 0,
                x: -20,
                filter: "blur(6px)",
                duration: 0.8,
                ease: "back.out(1.2)",
            }, "<0.3")
            .from(".header-nav-link", {
                opacity: 0,
                y: 10,
                filter: "blur(6px)",
                duration: 0.5,
                stagger: 0.1,
            }, ">-0.2");
    }, { scope: navbarRef });

    // ===============================
    // SCROLL HELPERS
    // ===============================
    const scrollToSection = (sectionId) => {
        if (!sectionId) return;
        const element = document.getElementById(sectionId);
        if (element) {
            const scrollToY =
                element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: scrollToY, behavior: "smooth" });
            setActiveLink(sectionId);
        }
    };

    const determineActiveSection = () => {
        if (location.pathname !== "/") return;

        const pageHeight = document.documentElement.scrollHeight;
        const scrollPosition = window.innerHeight + window.scrollY;

        if (scrollPosition >= pageHeight - 2) {
            setActiveLink("contact");
            return;
        }

        for (let i = sectionIds.length - 1; i >= 0; i--) {
            const id = sectionIds[i];
            const section = document.getElementById(id);
            if (section) {
                const rect = section.getBoundingClientRect();
                if (rect.top <= 120 && rect.bottom >= 120) {
                    setActiveLink(id);
                    return;
                }
            }
        }
    };

    // Add an event listener to call the function while scrolling
    // window.addEventListener('scroll', determineActiveSection);

    // old code:

    //Function to determine the active section while scrolling
    // const determineActiveSection = () => {
    //     for (let i = sectionIds.length - 1; i >= 0; i--) {
    //         const section = document.getElementById(sectionIds[i]);
    //         if (section) {
    //             const rect = section.getBoundingClientRect();
    //             if (rect.top <= 120 && rect.bottom >= 120) {
    //                 //set the active based on the section id
    //                 setActiveLink(sectionIds[i]);
    //                 break;
    //             }
    //         }
    //     }
    // };


    // ===============================
    // ACTIVE LINK ON SCROLL
    // ===============================
    useEffect(() => {
        if (location.pathname !== "/") {
            setActiveLink("");
            return;
        }

        determineActiveSection();

        const handleScroll = () => {
            determineActiveSection();
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // ===============================
    // NAVBAR CONTROLS (FIXED)
    // ===============================
    const closeNavbar = useCallback(() => {
        setNavbarOpen(false);
    }, [setNavbarOpen]);

    const toggleNavbar = () => {
        setNavbarOpen(!navbarOpen);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                navbarRef.current &&
                !navbarRef.current.contains(event.target)
            ) {
                closeNavbar();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [closeNavbar]);

    // ===============================
    // HANDLE HASH / STATE SCROLL
    // ===============================
    useEffect(() => {
        if (location.pathname !== "/") return;

        // detect hash first (e.g. "/#contact")
        const hash = location.hash ? location.hash.replace("#", "") : null;
        const stateTarget = location.state && location.state.scrollTo ? location.state.scrollTo : null;
        const targetId = hash || stateTarget;

        if (!targetId) return;

        let tries = 0;
        const maxTries = 20;

        const attempt = () => {
            const el = document.getElementById(targetId);
            if (el) {
                scrollToSection(targetId);
                navigate(location.pathname, { replace: true, state: null });
                return;
            }
            tries++;
            if (tries <= maxTries) {
                setTimeout(attempt, 75);
            }
        };

        attempt();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.hash, location.state]);

    // ===============================
    // NAV CLICK
    // ===============================
    const handleNavClick = (sectionId) => {
        closeNavbar();

        if (sectionId === "resume") {
            window.open(resume, "_blank");
            return;
        }

        if (location.pathname === "/") {
            scrollToSection(sectionId);
            return;
        }

        navigate("/", { state: { scrollTo: sectionId } });
    };

    // ===============================
    // JSX
    // ===============================
    return (
        <nav ref={navbarRef} className="sticky top-5 mx-5 md:mx-10 z-10">
            <div className="relative max-w-screen-2xl flex flex-wrap md:flex-nowrap items-center justify-between mx-auto py-1.5 md:py-2 border border-[#3f403e] px-4 rounded-[33px]">
                <div className='navbar-bg absolute w-full h-full left-0 bg-[#ffffff50] dark:bg-[#00000050] rounded-[33px] -z-[1]' />
                <svg style={{ display: "none" }}>
                    <filter id="displacementFilter">
                        <feImage href="data:image/png;base64,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" preserveAspectRatio="none" />
                        <feDisplacementMap
                            in="SourceGraphic"
                            scale="500"
                            xChannelSelector="R"
                            yChannelSelector="G"
                        />
                    </filter>
                </svg>
                <Link to="/" className="flex items-center space-x-3 rtl:space-x-reverse mx-auto md:mx-0 relative z-[2]" onClick={closeNavbar}>
                    <p className="header-logo-text font-andika self-center text-2xl sm:text-3xl md:text-4xl font-semibold whitespace-nowrap dark:text-tx-col">
                        <span className='text-y-col'>&lt;</span>Darshik<span className='text-y-col'>/&gt;</span>
                    </p>
                </Link>
                {/* mode component */}
                {/* <DarkMode /> */}
                {/* <button data-collapse-toggle="navbar-default" type="button" className="absolute z-[2] end-3 inline-flex items-center p-1 w-10 h-10 justify-center text-sm rounded-lg md:hidden text-gray-900 focus:outline-none focus:ring-0 focus:ring-gray-300 dark:text-white dark:focus:ring-gray-600" aria-controls="navbar-default" aria-expanded={navbarOpen} onClick={toggleNavbar}>
                    <span className="sr-only">Open main menu</span>
                    {/* <label className="absolute end-3 inline-flex items-center p-1 w-10 h-10 justify-center text-sm rounded-lg md:hidden text-gray-900 focus:outline-none focus:ring-0 focus:ring-gray-300 dark:text-white dark:focus:ring-gray-600">
                        <div className="w-9 h-10 cursor-pointer flex flex-col items-center justify-center">
                            <input
                                checked={navbarOpen}
                                onClick={toggleNavbar}
                                data-collapse-toggle="navbar-default"
                                aria-controls="navbar-default"
                                aria-expanded={navbarOpen}
                                className="hidden peer"
                                type="checkbox"
                            />
                            <div className="w-[50%] h-[2px] bg-black dark:bg-white rounded-sm transition-all duration-300 origin-left translate-y-[0.45rem] peer-checked:rotate-[-41deg]" />
                            <div className="w-[50%] h-[2px] bg-black dark:bg-white rounded-md transition-all duration-300 origin-center peer-checked:hidden" />
                            <div className="w-[50%] h-[2px] bg-black dark:bg-white rounded-md transition-all duration-300 origin-left -translate-y-[0.45rem] peer-checked:rotate-[41deg]" />
                        </div>
                    </label> 
                    {/* {navbarOpen ?
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                        </svg>
                        :
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path>
                        </svg>
                    } 
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d={`${navbarOpen ?
                            "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            :
                            "M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                            }`} clipRule="evenodd"></path>
                    </svg>
                </button> */}
                {/* <button className="flex flex-col justify-center items-center w-8 h-8 relative" data-collapse-toggle="navbar-default" type="button" aria-controls="navbar-default" aria-expanded={navbarOpen} onClick={toggleNavbar}>
                    <span className={`absolute h-[2px] w-6 bg-black dark:bg-white rounded transition-all duration-300 ease-[cubic-bezier(.65,.05,.36,1)] ${navbarOpen ? "rotate-[45deg] translate-y-0" : "-translate-y-[6px]"}`} />
                    <span className={`absolute h-[2px] w-6 bg-black dark:bg-white rounded transition-all duration-300 ease-[cubic-bezier(.65,.05,.36,1)] ${navbarOpen ? "scale-x-0 opacity-0" : "scale-x-100 opacity-100"}`} />
                    <span className={`absolute h-[2px] w-6 bg-black dark:bg-white rounded transition-all duration-300 ease-[cubic-bezier(.65,.05,.36,1)] ${navbarOpen ? "rotate-[-45deg] translate-y-0" : "translate-y-[6px]"}`} />
                </button> */}
                <button
                    className="absolute z-[2] end-3 md:hidden flex flex-col justify-center items-center w-6 h-6 text-black dark:text-white gap-[var(--gap)] [--thickness:2px] [--gap:6px] [--rotate:-45] [--w:100%]"
                    aria-expanded={navbarOpen}
                    onClick={toggleNavbar}
                >
                    {/* Top Line */}
                    <span
                        className={`h-[var(--thickness)] w-[--w] bg-current dark:bg-current rounded
                            transition-all duration-300 ease-[cubic-bezier(.65,.05,.36,1)]
                            ${navbarOpen
                                ? "rotate-[calc(var(--rotate)*_-1deg)] translate-y-[calc(var(--thickness)+var(--gap))]"
                                : ""
                            }`}
                    />

                    {/* Middle Line */}
                    <span
                        className={`h-[var(--thickness)] w-[--w] bg-current dark:bg-current rounded
                            transition-all duration-300 ease-[cubic-bezier(.65,.05,.36,1)]
                            ${navbarOpen
                                ? "scale-x-0 opacity-0"
                                : "scale-x-100 opacity-100"
                            }`}
                    />

                    {/* Bottom Line */}
                    <span
                        className={`h-[var(--thickness)] w-[--w] bg-current dark:bg-current rounded
                            transition-all duration-300 ease-[cubic-bezier(.65,.05,.36,1)]
                            ${navbarOpen
                                ? "rotate-[calc(var(--rotate)*_1deg)] translate-y-[calc((var(--thickness)+var(--gap))*-1)]"
                                : ""
                            }`}
                    />
                </button>
                <div className={`${navbarOpen ? 'pointer-events-auto top-[calc(100%-65.6px)] sm:top-[calc(100%-69.6px)]' : 'pointer-events-none top-[calc(-100dvh-1.25rem)]'} w-[calc(100%+2.5rem)] md:w-full h-dvh md:h-auto md:block fixed md:relative -z-[2] md:z-auto -left-5 md:left-0 md:top-0 transition-all ease-in md:opacity-100 md:pointer-events-auto text-2xl sm:text-3xl md:text-base`} id="navbar-default">
                    <ul className="font-medium flex flex-col justify-center items-center pt-[81.7px] sm:pt-[85.7px] md:pt-0 p-4 md:p-0 h-full border-0 border-gray-100 bg-[#ffffff75] md:flex-row md:space-x-0 rtl:space-x-reverse md:!border-0 dark:text-tx-col dark:border-[#3f403e] dark:bg-[#00000075] md:bg-transparent md:dark:bg-transparent">
                        {sectionIds.map((sectionId, i) => (
                            <li
                                className={`header-nav-link block py-1.5 px-3.5 md:py-0 md:px-5 rounded-lg capitalize text-center relative md:ml-auto ${i === 2 ? 'md:!mr-auto' : ''} ${sectionId === 'contact' ? `md:border-2 md:border-y-col md:!px-6 md:rounded-full md:py-1 md:hover:border-y-col md:hover:!bg-y-col md:cursor-pointer ${activeLink === 'contact' ? 'md:hover:!bg-opacity-90' : 'md:hover:!bg-opacity-60'}` : ''}
                                            ${activeLink === sectionId ?
                                        `text-blue-700 dark:text-blue-500 ${activeLink === 'contact' ? 'activedc' : 'actived'}`
                                        :
                                        'hover:text-blue-700 dark:hover:text-blue-500'
                                    }
                                        `}
                                onClick={() => scrollToSection(sectionId)}
                                key={i}
                            >
                                <Link className='w-full inline-block'
                                    to={sectionId === 'resume' ? resume : '/'} target={sectionId === 'resume' ? '_blank' : ''}
                                    onClick={() => handleNavClick(sectionId)}
                                >
                                    {sectionId}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    {/* <ul className="font-medium flex flex-col p-4 md:p-0 border border-gray-100 bg-[#ffffff70] rounded-[18px] md:flex-row md:space-x-8 rtl:space-x-reverse md:!border-0 dark:text-tx-col dark:border-gray-700 dark:bg-[#37415170] md:bg-transparent md:dark:bg-transparent">
                            <li>
                                <a href="#Work" className="block py-2 px-3 bg-blue-700 rounded-lg md:bg-transparent md:text-blue-700 md:p-0 md:dark:text-blue-500" aria-current="page" onClick={closeNavbar}>Work</a>
                            </li>
                            <li>
                                <a href="#Resume" className="block py-2 px-3 rounded-lg hover:bg-gray-500 md:hover:bg-transparent md:border-0 md:hover:text-blue-700 md:p-0 md:dark:hover:text-blue-500 dark:hover:bg-gray-700 dark:hover:text-white md:dark:hover:bg-transparent" onClick={closeNavbar}>Resume</a>
                            </li>
                            <li>
                                <a href="#About" className="block py-2 px-3 rounded-lg hover:bg-gray-500 md:hover:bg-transparent md:border-0 md:hover:text-blue-700 md:p-0 md:dark:hover:text-blue-500 dark:hover:bg-gray-700 dark:hover:text-white md:dark:hover:bg-transparent" onClick={closeNavbar}>About</a>
                            </li>
                            <li>
                                <a href="#Contact" className="block py-2 px-3 rounded-lg hover:bg-gray-500 md:hover:bg-transparent md:border-0 md:hover:text-blue-700 md:p-0 md:dark:hover:text-blue-500 dark:hover:bg-gray-700 dark:hover:text-white md:dark:hover:bg-transparent" onClick={closeNavbar}>Contact</a>
                            </li>
                        </ul> */}
                </div>
            </div>
        </nav>
    );
};

export default Header;
